/**
 * 自由オブジェクトの局所座標 → 世界座標。
 * 先に局所 +Y を mount へ合わせ、次に世界 Y まわり yaw。
 */

import type { ObjectMountAxis, ObjectYaw, Vec3 } from './types';
import { localToWorldXZ, yawFromWorldXZ } from './view-grid';

function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function scale(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function length(a: Vec3): number {
  return Math.hypot(a.x, a.y, a.z);
}

function normalize(a: Vec3): Vec3 {
  const len = length(a);
  if (len < 1e-9) return { x: 0, y: 0, z: 0 };
  return { x: a.x / len, y: a.y / len, z: a.z / len };
}

export const OBJECT_YAWS: readonly ObjectYaw[] = [0, 90, 180, 270];
export const OBJECT_MOUNTS: readonly ObjectMountAxis[] = ['+x', '-x', '+y', '-y', '+z', '-z'];

const WALL_NORMAL_Y = 0.5;

export interface ObjectBounds {
  min: Vec3;
  max: Vec3;
}

export interface ObjectAabb {
  min: Vec3;
  max: Vec3;
}

export function isObjectYaw(value: number): value is ObjectYaw {
  return Number.isFinite(value);
}

export function normalizeObjectYaw(yaw: number): ObjectYaw {
  if (!Number.isFinite(yaw)) return 0;
  return ((yaw % 360) + 360) % 360;
}

export function isObjectMountAxis(value: string): value is ObjectMountAxis {
  return (OBJECT_MOUNTS as readonly string[]).includes(value);
}

export function mountAxisVector(mount: ObjectMountAxis): Vec3 {
  switch (mount) {
    case '+x':
      return { x: 1, y: 0, z: 0 };
    case '-x':
      return { x: -1, y: 0, z: 0 };
    case '+y':
      return { x: 0, y: 1, z: 0 };
    case '-y':
      return { x: 0, y: -1, z: 0 };
    case '+z':
      return { x: 0, y: 0, z: 1 };
    case '-z':
      return { x: 0, y: 0, z: -1 };
  }
}

/** いちばん長い成分の符号付き軸。 */
export function snapMountAxis(direction: Vec3): ObjectMountAxis {
  const ax = Math.abs(direction.x);
  const ay = Math.abs(direction.y);
  const az = Math.abs(direction.z);
  if (ax >= ay && ax >= az) return direction.x >= 0 ? '+x' : '-x';
  if (ay >= az) return direction.y >= 0 ? '+y' : '-y';
  return direction.z >= 0 ? '+z' : '-z';
}

/** 取り付け面は面の内側を向くので、外側法線の逆。 */
export function mountFromOutwardNormal(outward: Vec3): ObjectMountAxis {
  return snapMountAxis(scale(outward, -1));
}

function yawAroundY(point: Vec3, yaw: number): Vec3 {
  const xz = localToWorldXZ(point.x, point.z, yaw);
  return { x: xz.x, y: point.y, z: xz.z };
}

/** 局所 +Y を mount へ回す。 */
function applyMount(point: Vec3, mount: ObjectMountAxis): Vec3 {
  switch (mount) {
    case '+y':
      return point;
    case '-y':
      return { x: point.x, y: -point.y, z: -point.z };
    case '+x':
      return { x: point.y, y: -point.x, z: point.z };
    case '-x':
      return { x: -point.y, y: point.x, z: point.z };
    case '+z':
      return { x: point.x, y: -point.z, z: point.y };
    case '-z':
      return { x: point.x, y: point.z, z: -point.y };
  }
}

export function rotateLocalPoint(
  local: Vec3,
  yaw: number,
  mount: ObjectMountAxis = '+y'
): Vec3 {
  return yawAroundY(applyMount(local, mount), yaw);
}

export function objectWorldPoint(
  local: Vec3,
  origin: Vec3,
  yaw: number,
  mount: ObjectMountAxis = '+y'
): Vec3 {
  return add(origin, rotateLocalPoint(local, yaw, mount));
}

export function objectBoundsCenter(bounds: ObjectBounds): Vec3 {
  return {
    x: (bounds.min.x + bounds.max.x) * 0.5,
    y: (bounds.min.y + bounds.max.y) * 0.5,
    z: (bounds.min.z + bounds.max.z) * 0.5,
  };
}

export function objectWorldAabb(
  bounds: ObjectBounds,
  origin: Vec3,
  yaw: number,
  mount: ObjectMountAxis = '+y'
): ObjectAabb {
  const corners: Vec3[] = [];
  for (const x of [bounds.min.x, bounds.max.x]) {
    for (const y of [bounds.min.y, bounds.max.y]) {
      for (const z of [bounds.min.z, bounds.max.z]) {
        corners.push(objectWorldPoint({ x, y, z }, origin, yaw, mount));
      }
    }
  }
  const min = { x: Infinity, y: Infinity, z: Infinity };
  const max = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const corner of corners) {
    min.x = Math.min(min.x, corner.x);
    min.y = Math.min(min.y, corner.y);
    min.z = Math.min(min.z, corner.z);
    max.x = Math.max(max.x, corner.x);
    max.y = Math.max(max.y, corner.y);
    max.z = Math.max(max.z, corner.z);
  }
  return { min, max };
}

/** 局所 +Z が dir の XZ に最も近づく 90° yaw。 */
export function yawFacingXZ(dir: Vec3): ObjectYaw {
  let best: ObjectYaw = 0;
  let bestDot = -Infinity;
  for (const yaw of OBJECT_YAWS) {
    const forward = rotateLocalPoint({ x: 0, y: 0, z: 1 }, yaw, '+y');
    const score = forward.x * dir.x + forward.z * dir.z;
    if (score > bestDot) {
      bestDot = score;
      best = yaw;
    }
  }
  return best;
}

/** 水平ベクトル from を to に重ねる世界 Y yaw。 */
function yawMappingHorizontal(from: Vec3, to: Vec3): ObjectYaw {
  if (Math.hypot(from.x, from.z) < 1e-9 || Math.hypot(to.x, to.z) < 1e-9) return 0;
  return normalizeObjectYaw(yawFromWorldXZ(to.x, to.z) - yawFromWorldXZ(from.x, from.z));
}

/**
 * 裏面（局所 +Y）を面の内側へ向け、+X を格子の面内成分へ寄せる。
 * 床・天井は ±Y、壁は ±Z を傾けてから世界 Y で回す。
 */
export function surfaceMountPose(
  outward: Vec3,
  snapYaw = 0
): { yaw: ObjectYaw; mount: ObjectMountAxis } {
  const n = normalize(outward);
  const into = length(n) < 1e-9 ? { x: 0, y: -1, z: 0 } : scale(n, -1);
  const latticeYaw = normalizeObjectYaw(snapYaw);

  if (Math.abs(into.y) >= WALL_NORMAL_Y) {
    return { mount: into.y >= 0 ? '+y' : '-y', yaw: latticeYaw };
  }

  const target = normalize({ x: into.x, y: 0, z: into.z });
  const xz = localToWorldXZ(1, 0, latticeYaw);
  const lattice = { x: xz.x, y: 0, z: xz.z };
  const alongRaw = sub(lattice, scale(target, lattice.x * target.x + lattice.z * target.z));
  const along =
    Math.hypot(alongRaw.x, alongRaw.z) < 1e-6
      ? { x: -target.z, y: 0, z: target.x }
      : normalize({ x: alongRaw.x, y: 0, z: alongRaw.z });

  let bestMount: ObjectMountAxis = '-z';
  let bestYaw = 0;
  let bestScore = -Infinity;
  for (const mount of ['-z', '+z'] as const) {
    const mountYaw = yawMappingHorizontal(applyMount({ x: 0, y: 1, z: 0 }, mount), target);
    const back = rotateLocalPoint({ x: 0, y: 1, z: 0 }, mountYaw, mount);
    const axis = rotateLocalPoint({ x: 1, y: 0, z: 0 }, mountYaw, mount);
    const score = dot(back, target) * 2 + dot(axis, along);
    if (score > bestScore) {
      bestScore = score;
      bestMount = mount;
      bestYaw = mountYaw;
    }
  }
  return { mount: bestMount, yaw: bestYaw };
}

/** 取り付け軸まわりで、局所 +Z が視線の面内成分に最も近づく yaw。 */
export function yawFacingOnMount(look: Vec3, mount: ObjectMountAxis): ObjectYaw {
  const axis = mountAxisVector(mount);
  const projected = sub(look, scale(axis, dot(look, axis)));
  const fallback = { x: 0, y: 0, z: -1 };
  const target =
    length(projected) < 1e-6
      ? normalize(sub(fallback, scale(axis, dot(fallback, axis))))
      : normalize(projected);
  if (length(target) < 1e-6) return 0;
  let best: ObjectYaw = 0;
  let bestDot = -Infinity;
  for (const yaw of OBJECT_YAWS) {
    const forward = rotateLocalPoint({ x: 0, y: 0, z: 1 }, yaw, mount);
    const score = dot(forward, target);
    if (score > bestDot) {
      bestDot = score;
      best = yaw;
    }
  }
  return best;
}
